import { useEffect, useMemo, useState } from 'react';
import {
  ArrowRight,
  BadgeCheck,
  BookOpen,
  BriefcaseMedical,
  CalendarDays,
  Car,
  Check,
  ChevronRight,
  Clock,
  FileText,
  Gavel,
  Globe2,
  HeartHandshake,
  Landmark,
  MapPin,
  Menu,
  MessageCircle,
  Phone,
  Scale,
  ShieldCheck,
  Sparkles,
  Star,
  Users,
  X
} from 'lucide-react';
import {
  areaPages,
  blogArticles,
  bookingTopics,
  findArea,
  findArticle,
  getMetaForPath,
  homeMeta,
  navLinks,
  normalizePath,
  site,
  whatsappUrl
} from './siteData.js';

const iconMap = {
  BriefcaseMedical,
  Car,
  Gavel,
  Globe2,
  HeartHandshake,
  Landmark
};

function Icon({ name, className }) {
  const Selected = iconMap[name] || Scale;
  return <Selected className={className} aria-hidden="true" />;
}

function setTag(selector, attr, value) {
  if (!value) return;
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    if (selector.includes('property=')) {
      element.setAttribute('property', selector.match(/"([^"]+)"/)?.[1] || '');
    } else {
      element.setAttribute('name', selector.match(/"([^"]+)"/)?.[1] || '');
    }
    document.head.appendChild(element);
  }
  element.setAttribute(attr, value);
}

function setCanonical(url) {
  let canonical = document.head.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }
  canonical.setAttribute('href', url);
}

function articlePath(article) {
  return article.path || `/blog/${article.slug}`;
}

function findArticleInList(path, articles) {
  const normalized = normalizePath(path);
  return articles.find((article) => articlePath(article) === normalized);
}

function getMetaForCurrentPath(path, articles) {
  const article = findArticleInList(path, articles);
  if (article) {
    return {
      path: articlePath(article),
      metaTitle: article.metaTitle || `${article.title} | Avv. Salucci`,
      metaDescription: article.description,
      image: article.image
    };
  }
  return getMetaForPath(path);
}

function updateHead(path, articles) {
  const meta = getMetaForCurrentPath(path, articles);
  const canonical = `${site.siteUrl}${meta.path === '/' ? '/' : meta.path}`;
  const image = meta.image || site.heroImage;
  document.title = meta.metaTitle;
  setTag('meta[name="description"]', 'content', meta.metaDescription);
  setTag('meta[property="og:title"]', 'content', meta.metaTitle);
  setTag('meta[property="og:description"]', 'content', meta.metaDescription);
  setTag('meta[property="og:type"]', 'content', 'website');
  setTag('meta[property="og:url"]', 'content', canonical);
  setTag('meta[property="og:image"]', 'content', image.startsWith('http') ? image : `${site.siteUrl}${image}`);
  setTag('meta[name="twitter:card"]', 'content', 'summary_large_image');
  setCanonical(canonical);
}

function App() {
  const [path, setPath] = useState(() => normalizePath(window.location.pathname));
  const [blogPosts, setBlogPosts] = useState(blogArticles);
  const [blogLoaded, setBlogLoaded] = useState(false);

  useEffect(() => {
    const handlePop = () => setPath(normalizePath(window.location.pathname));
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetch('/api/blog/posts', { signal: controller.signal })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (Array.isArray(data?.posts) && data.posts.length) {
          setBlogPosts(data.posts);
        }
      })
      .catch(() => {})
      .finally(() => setBlogLoaded(true));
    return () => controller.abort();
  }, []);

  useEffect(() => {
    updateHead(path, blogPosts);
    updateStructuredData(path, blogPosts);
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [path, blogPosts]);

  const navigate = (href) => {
    if (!href || href.startsWith('http') || href.startsWith('tel:') || href.startsWith('mailto:')) return;
    const target = new URL(href, window.location.origin);
    window.history.pushState({}, '', `${target.pathname}${target.hash}`);
    setPath(normalizePath(target.pathname));
    if (target.hash) {
      setTimeout(() => {
        document.querySelector(target.hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 80);
    }
  };

  const route = useMemo(() => {
    if (path === '/') return <HomePage navigate={navigate} articles={blogPosts} />;
    if (path === '/blog') return <BlogPage navigate={navigate} articles={blogPosts} />;
    if (path === '/contatti') return <ContactPage navigate={navigate} />;
    if (path === '/credits') return <CreditsPage navigate={navigate} />;
    const area = findArea(path);
    if (area) return <AreaPage area={area} navigate={navigate} />;
    const article = findArticleInList(path, blogPosts) || findArticle(path);
    if (article) return <ArticlePage article={article} navigate={navigate} />;
    if (path.startsWith('/blog/') && !blogLoaded) return <LoadingArticle />;
    return <NotFound navigate={navigate} />;
  }, [path, blogPosts, blogLoaded]);

  return (
    <div className="min-h-screen bg-parchment text-ink">
      <Header navigate={navigate} />
      {route}
      <Footer navigate={navigate} />
    </div>
  );
}

function SmartLink({ href, navigate, children, className = '', onClick, ...props }) {
  return (
    <a
      href={href}
      className={className}
      onClick={(event) => {
        onClick?.(event);
        if (event.defaultPrevented) return;
        if (href?.startsWith('/') || href?.startsWith('#')) {
          event.preventDefault();
          navigate(href.startsWith('#') ? `/${href}` : href);
        }
      }}
      {...props}
    >
      {children}
    </a>
  );
}

function Logo({ navigate, light = false }) {
  return (
    <SmartLink
      href="/"
      navigate={navigate}
      className="group flex min-w-0 items-center gap-3"
      aria-label={`${site.brandName} homepage`}
    >
      <img
        src={site.logo}
        alt=""
        className="h-11 w-11 shrink-0 rounded-lg shadow-sm ring-1 ring-white/20"
        width="44"
        height="44"
      />
      <span className="min-w-0 leading-tight">
        <span
          className={`block truncate font-serif text-xl font-semibold ${
            light ? 'text-white' : 'text-forest'
          }`}
        >
          Avvocato Federico Salucci
        </span>
        <span className={`block truncate text-xs uppercase tracking-[0.22em] ${light ? 'text-white/70' : 'text-forest/65'}`}>
          Studio legale
        </span>
      </span>
    </SmartLink>
  );
}

function Header({ navigate }) {
  const [open, setOpen] = useState(false);

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/35 bg-paper/90 backdrop-blur-xl">
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Logo navigate={navigate} />
        <nav className="hidden items-center gap-1 lg:flex" aria-label="Navigazione principale">
          {navLinks.map((link) => (
            <SmartLink
              key={link.href}
              href={link.href}
              navigate={navigate}
              className="rounded-lg px-4 py-2 text-sm font-medium text-forest/78 transition hover:bg-forest/8 hover:text-forest"
            >
              {link.label}
            </SmartLink>
          ))}
        </nav>
        <div className="hidden items-center gap-3 lg:flex">
          <a
            href={`tel:${site.phoneHref}`}
            className="inline-flex h-11 items-center gap-2 rounded-lg border border-forest/15 px-4 text-sm font-semibold text-forest transition hover:border-forest/35 hover:bg-forest/5"
          >
            <Phone className="h-4 w-4" />
            {site.phoneLabel}
          </a>
          <SmartLink
            href="/contatti"
            navigate={navigate}
            className="inline-flex h-11 items-center gap-2 rounded-lg bg-forest px-5 text-sm font-semibold text-white shadow-soft transition hover:bg-pine"
          >
            <CalendarDays className="h-4 w-4" />
            Appuntamento
          </SmartLink>
        </div>
        <button
          className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-forest/15 text-forest lg:hidden"
          type="button"
          aria-label={open ? 'Chiudi menu' : 'Apri menu'}
          onClick={() => setOpen((value) => !value)}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      {open && (
        <div className="border-t border-forest/10 bg-paper px-4 pb-5 pt-2 lg:hidden">
          <nav className="mx-auto grid max-w-7xl gap-1" aria-label="Navigazione mobile">
            {navLinks.map((link) => (
              <SmartLink
                key={link.href}
                href={link.href}
                navigate={navigate}
                onClick={() => setOpen(false)}
                className="rounded-lg px-3 py-3 text-base font-semibold text-forest hover:bg-forest/8"
              >
                {link.label}
              </SmartLink>
            ))}
            <div className="mt-3 grid grid-cols-2 gap-2">
              <a
                href={`tel:${site.phoneHref}`}
                className="inline-flex h-12 items-center justify-center gap-2 rounded-lg border border-forest/15 text-sm font-semibold text-forest"
              >
                <Phone className="h-4 w-4" />
                Chiama
              </a>
              <SmartLink
                href="/contatti"
                navigate={navigate}
                onClick={() => setOpen(false)}
                className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-forest text-sm font-semibold text-white"
              >
                <CalendarDays className="h-4 w-4" />
                Prenota
              </SmartLink>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}

function HomePage({ navigate, articles }) {
  return (
    <main>
      <section className="relative flex min-h-[760px] items-center overflow-hidden bg-pine pt-28 text-white">
        <img
          src={site.heroImage}
          alt="Studio legale, documenti e bilancia della giustizia"
          className="absolute inset-0 h-full w-full object-cover object-center brightness-[0.82] contrast-105"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(20,40,39,0.94)_0%,rgba(38,63,61,0.82)_38%,rgba(38,63,61,0.45)_64%,rgba(38,63,61,0.10)_100%)]" />
        <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-pine/55 to-transparent" />
        <div className="relative mx-auto w-full max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="mb-6 flex items-center gap-3">
              <img
                src={site.portraitImage}
                alt="Avvocato Federico Salucci"
                className="h-20 w-20 shrink-0 rounded-full object-cover object-[center_24%] grayscale brightness-125 contrast-105 ring-2 ring-white/65 shadow-md"
                loading="eager"
              />
              <div className="inline-flex items-center gap-2 border-l-4 border-brass bg-white/10 px-4 py-3 text-sm font-bold text-white shadow-sm backdrop-blur">
                <MapPin className="h-4 w-4 text-brass" />
                Studio legale
              </div>
            </div>
            <h1 className="text-5xl font-extrabold leading-[0.98] text-white drop-shadow-[0_3px_18px_rgba(0,0,0,0.45)] sm:text-6xl lg:text-7xl">
              {homeMeta.headline}
            </h1>
            <p className="mt-6 text-lg leading-8 text-white/86 drop-shadow-[0_2px_12px_rgba(0,0,0,0.42)] sm:text-xl">
              {homeMeta.description}
            </p>
            <div className="mt-6 flex flex-wrap gap-2 text-sm font-bold text-white/82">
              <span className="bg-white/12 px-3 py-2 backdrop-blur">Diritto di famiglia</span>
              <span className="bg-white/12 px-3 py-2 backdrop-blur">Penale</span>
              <span className="bg-white/12 px-3 py-2 backdrop-blur">Lavoro e invalidità</span>
              <span className="bg-white/12 px-3 py-2 backdrop-blur">Sinistri stradali</span>
            </div>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <SmartLink
                href="/contatti"
                navigate={navigate}
                className="inline-flex h-13 items-center justify-center gap-3 rounded-lg bg-white px-6 text-base font-bold text-forest shadow-soft transition hover:bg-parchment"
              >
                <CalendarDays className="h-5 w-5" />
                Richiedi appuntamento
              </SmartLink>
              <a
                href={`tel:${site.phoneHref}`}
                className="inline-flex h-13 items-center justify-center gap-3 rounded-lg border border-white/30 bg-white/10 px-6 text-base font-bold text-white backdrop-blur transition hover:bg-white/16"
              >
                <Phone className="h-5 w-5" />
                Chiama ora
              </a>
            </div>
          </div>
        </div>
      </section>

      <QuickFacts />
      <AreaGrid navigate={navigate} />
      <MethodSection />
      <AboutSection navigate={navigate} />
      <BookingSection />
      <BlogPreview navigate={navigate} articles={articles} />
    </main>
  );
}

function QuickFacts() {
  const facts = [
    { icon: MapPin, label: 'Dove riceve', value: 'Roma e Viterbo' },
    { icon: Phone, label: 'Primo contatto', value: site.phoneLabel },
    { icon: CalendarDays, label: 'Prenotazione', value: 'Semplice e diretta' },
    { icon: FileText, label: 'Prima consulenza', value: 'Conoscitiva gratuita' }
  ];
  return (
    <section className="bg-paper">
      <div className="mx-auto grid max-w-7xl gap-px px-4 py-4 sm:px-6 md:grid-cols-2 lg:grid-cols-4 lg:px-8">
        {facts.map((fact) => (
          <div key={fact.label} className="flex items-start gap-4 bg-paper px-3 py-4">
            <fact.icon className="mt-1 h-5 w-5 shrink-0 text-brass" />
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-forest/55">{fact.label}</p>
              <p className="mt-1 text-sm font-semibold text-forest">{fact.value}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function SectionIntro({ eyebrow, title, text, align = 'left', light = false }) {
  return (
    <div className={align === 'center' ? 'mx-auto max-w-3xl text-center' : 'max-w-3xl'}>
      <p className="text-sm font-bold uppercase tracking-[0.2em] text-brass">{eyebrow}</p>
      <h2 className={`mt-3 text-4xl font-extrabold leading-tight sm:text-5xl ${light ? 'text-white' : 'text-forest'}`}>
        {title}
      </h2>
      {text && <p className={`mt-5 text-lg leading-8 ${light ? 'text-white/72' : 'text-ink/70'}`}>{text}</p>}
    </div>
  );
}

function AreaGrid({ navigate }) {
  return (
    <section id="aree" className="bg-parchment py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionIntro
          eyebrow="Come possiamo aiutarti"
          title="Di cosa hai bisogno?"
          text="Scegli l’area più vicina alla tua situazione e scopri come lo Studio può aiutarti a capire il problema, valutare le soluzioni possibili e muovere i primi passi."
        />
        <div className="mt-12 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {areaPages.map((area) => (
            <SmartLink
              key={area.path}
              href={area.path}
              navigate={navigate}
              className="group overflow-hidden rounded-lg border border-forest/10 bg-paper shadow-sm transition hover:-translate-y-1 hover:shadow-soft"
            >
              <div className="relative h-52 overflow-hidden">
                <img
                  src={area.image}
                  alt=""
                  className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-forest/85 via-forest/25 to-transparent" />
                <div className="absolute bottom-4 left-4 inline-flex h-12 w-12 items-center justify-center rounded-lg bg-paper text-forest shadow-lg">
                  <Icon name={area.icon} className="h-6 w-6" />
                </div>
              </div>
              <div className="p-6">
                <p className="text-sm font-bold uppercase tracking-[0.18em] text-brass">{area.eyebrow}</p>
                <h3 className="mt-3 text-2xl font-extrabold leading-tight text-forest">{area.title}</h3>
                <p className="mt-3 min-h-24 text-base leading-7 text-ink/68">{area.description}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-wine">
                  Entra nell’area
                  <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
                </span>
              </div>
            </SmartLink>
          ))}
        </div>
      </div>
    </section>
  );
}

function MethodSection() {
  const steps = [
    {
      icon: FileText,
      title: 'Racconti il problema',
      text: 'Durante il primo confronto vengono raccolti i fatti, i documenti disponibili e le eventuali urgenze.'
    },
    {
      icon: Scale,
      title: 'Capisci le possibilità',
      text: 'Lo Studio ti spiega quali strade sono percorribili, quali tempi aspettarti e quali rischi valutare.'
    },
    {
      icon: Users,
      title: 'Si prepara il percorso',
      text: 'Quando serve, vengono coinvolti consulenti, medici legali o periti per rafforzare la pratica.'
    },
    {
      icon: BadgeCheck,
      title: 'Resti aggiornato',
      text: 'Ogni passaggio viene spiegato in modo chiaro, così sai cosa sta succedendo e perché.'
    }
  ];

  return (
    <section id="metodo" className="bg-forest py-20 text-white sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
          <SectionIntro
            eyebrow="Come funziona"
            title="Un confronto chiaro prima di prendere decisioni."
            text="Quando hai un problema legale, la prima cosa importante è capire dove ti trovi: quali documenti servono, quali soluzioni esistono e quale passo conviene fare."
            light
          />
          <div className="grid gap-4 sm:grid-cols-2">
            {steps.map((step, index) => (
              <div key={step.title} className="rounded-lg border border-white/12 bg-white/8 p-6">
                <div className="flex items-center justify-between gap-4">
                  <step.icon className="h-7 w-7 text-brass" />
                  <span className="font-serif text-4xl text-white/18">0{index + 1}</span>
                </div>
                <h3 className="mt-6 text-xl font-semibold">{step.title}</h3>
                <p className="mt-3 leading-7 text-white/72">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function AboutSection({ navigate }) {
  return (
    <section id="avvocato" className="bg-paper py-20 sm:py-24">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-8 sm:flex-row sm:items-start">
          <div className="relative aspect-[4/3] w-full shrink-0 overflow-hidden rounded-lg shadow-soft sm:aspect-square sm:w-64">
            <div className="absolute bottom-4 left-4 right-4 z-10 bg-pine/82 px-4 py-3 backdrop-blur">
              <p className="text-sm font-extrabold text-white">Avvocato Federico Salucci</p>
            </div>
            <img
              src={site.portraitImage}
              alt="Avvocato Federico Salucci"
              className="absolute inset-0 h-full w-full object-cover object-[center_22%] grayscale brightness-110 contrast-105"
              loading="lazy"
            />
          </div>
          <div className="min-w-0">
          <SectionIntro
            eyebrow="Profilo"
            title="Esperienza legale al tuo fianco."
            text="L’Avvocato Federico Salucci si è laureato in Giurisprudenza presso l’Università degli Studi della Tuscia con una tesi in diritto del lavoro dedicata a malattie professionali e invalidità civile."
          />
          </div>
        </div>
        <div className="mt-8">
          <div className="mt-7 space-y-5 text-base leading-8 text-ink/72">
            <p>
              Dopo la pratica forense e l’attività presso studi legali operanti nei settori civile e penale,
              ha consolidato la propria esperienza nella gestione di procedimenti giudiziali e stragiudiziali.
            </p>
            <p>
              A Roma ha frequentato la Scuola Greco Pittella per la preparazione all’esame di abilitazione,
              approfondendo redazione degli atti, strategia processuale e questioni rilevanti di diritto civile e penale.
            </p>
            <p>
              Ha inoltre frequentato l’Osservatorio sul Diritto di Famiglia e collabora con professionisti e
              consulenti tecnici per garantire un’assistenza completa nei casi che richiedono competenze multidisciplinari.
            </p>
          </div>
          <div className="mt-8 grid gap-3 sm:grid-cols-2">
            {[
              'Prima consulenza conoscitiva gratuita',
              'Sede principale in Roma',
              'Appuntamenti anche a Viterbo',
              'Consulenze online in Italia e dall’estero'
            ].map((item) => (
              <div key={item} className="flex items-start gap-3 rounded-lg border border-forest/10 bg-parchment p-4">
                <Check className="mt-1 h-5 w-5 shrink-0 text-brass" />
                <span className="text-sm font-semibold leading-6 text-forest">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function BookingSection() {
  return (
    <section id="prenota" className="bg-parchment py-20 sm:py-24">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <SectionIntro
          eyebrow="Contatti"
          title="Contatta lo Studio"
          text="Descrivi brevemente la tua situazione: lo Studio ti ricontatterà per capire come procedere e fissare un primo confronto."
          align="center"
        />
        <div className="mt-10">
          <AppointmentForm />
        </div>
      </div>
    </section>
  );
}

function ContactRow({ icon: RowIcon, title, text, href }) {
  const content = (
    <div className="flex items-start gap-4 rounded-lg border border-forest/10 bg-paper p-5 transition hover:border-forest/20">
      <RowIcon className="mt-1 h-5 w-5 shrink-0 text-brass" />
      <div>
        <p className="text-sm font-bold uppercase tracking-[0.16em] text-forest/55">{title}</p>
        <p className="mt-1 font-semibold text-forest">{text}</p>
      </div>
    </div>
  );
  if (!href) return content;
  return (
    <a href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noreferrer">
      {content}
    </a>
  );
}

function AppointmentForm() {
  const [values, setValues] = useState({
    name: '',
    phone: '',
    topic: bookingTopics[0],
    mode: 'In studio a Roma',
    date: '',
    message: ''
  });

  const update = (key, value) => setValues((current) => ({ ...current, [key]: value }));

  const submit = (event) => {
    event.preventDefault();
    const message = [
      'Buongiorno Avv. Salucci, vorrei richiedere un appuntamento.',
      values.name ? `Nome: ${values.name}` : '',
      values.phone ? `Telefono: ${values.phone}` : '',
      `Area: ${values.topic}`,
      `Tipo di appuntamento preferito: ${values.mode}`,
      values.date ? `Preferenza giorno/orario: ${values.date}` : '',
      values.message ? `Messaggio: ${values.message}` : ''
    ]
      .filter(Boolean)
      .join('\n');
    window.location.href = whatsappUrl(message);
  };

  return (
    <form onSubmit={submit} className="rounded-lg border border-forest/10 bg-paper p-5 shadow-soft sm:p-7">
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="grid gap-2 text-sm font-semibold text-forest">
          Nome e cognome
          <input
            value={values.name}
            onChange={(event) => update('name', event.target.value)}
            className="h-12 rounded-lg border border-forest/15 bg-white px-4 text-base font-medium outline-none transition focus:border-brass focus:ring-4 focus:ring-brass/15"
            placeholder="Il tuo nome"
          />
        </label>
        <label className="grid gap-2 text-sm font-semibold text-forest">
          Telefono
          <input
            value={values.phone}
            onChange={(event) => update('phone', event.target.value)}
            className="h-12 rounded-lg border border-forest/15 bg-white px-4 text-base font-medium outline-none transition focus:border-brass focus:ring-4 focus:ring-brass/15"
            placeholder="Numero di telefono"
            inputMode="tel"
          />
        </label>
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <label className="grid gap-2 text-sm font-semibold text-forest">
          Area di interesse
          <select
            value={values.topic}
            onChange={(event) => update('topic', event.target.value)}
            className="h-12 rounded-lg border border-forest/15 bg-white px-4 text-base font-medium outline-none transition focus:border-brass focus:ring-4 focus:ring-brass/15"
          >
            {bookingTopics.map((topic) => (
              <option key={topic}>{topic}</option>
            ))}
          </select>
        </label>
        <label className="grid gap-2 text-sm font-semibold text-forest">
          Tipo di appuntamento
          <select
            value={values.mode}
            onChange={(event) => update('mode', event.target.value)}
            className="h-12 rounded-lg border border-forest/15 bg-white px-4 text-base font-medium outline-none transition focus:border-brass focus:ring-4 focus:ring-brass/15"
          >
            <option>In studio a Roma</option>
            <option>A Viterbo previo appuntamento</option>
            <option>Consulenza online</option>
            <option>Telefonata preliminare</option>
          </select>
        </label>
      </div>
      <label className="mt-4 grid gap-2 text-sm font-semibold text-forest">
        Preferenza giorno/orario
        <input
          value={values.date}
          onChange={(event) => update('date', event.target.value)}
          className="h-12 rounded-lg border border-forest/15 bg-white px-4 text-base font-medium outline-none transition focus:border-brass focus:ring-4 focus:ring-brass/15"
          placeholder="Esempio: martedì pomeriggio"
        />
      </label>
      <label className="mt-4 grid gap-2 text-sm font-semibold text-forest">
        Breve descrizione
        <textarea
          value={values.message}
          onChange={(event) => update('message', event.target.value)}
          className="min-h-32 resize-y rounded-lg border border-forest/15 bg-white px-4 py-3 text-base font-medium outline-none transition focus:border-brass focus:ring-4 focus:ring-brass/15"
          placeholder="Scrivi in poche righe di cosa hai bisogno"
        />
      </label>
      <button
        type="submit"
        className="mt-5 inline-flex h-13 w-full items-center justify-center gap-3 rounded-lg bg-forest px-6 text-base font-bold text-white transition hover:bg-pine"
      >
        <ArrowRight className="h-5 w-5" />
        Invia richiesta
      </button>
    </form>
  );
}

function BlogPreview({ navigate, articles }) {
  return (
    <section className="bg-paper py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <SectionIntro
            eyebrow="Blog"
            title="Hai un dubbio legale?"
            text="Qui trovi spiegazioni semplici su situazioni comuni: cosa controllare, quali documenti preparare e quando può essere utile chiedere assistenza."
          />
          <SmartLink
            href="/blog"
            navigate={navigate}
            className="inline-flex h-12 items-center justify-center gap-2 rounded-lg border border-forest/15 px-5 text-sm font-bold text-forest transition hover:bg-forest/5"
          >
            Tutti gli articoli
            <ArrowRight className="h-4 w-4" />
          </SmartLink>
        </div>
        <div className="mt-10 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          {articles.slice(0, 4).map((article) => (
            <ArticleCard key={articlePath(article)} article={article} navigate={navigate} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ArticleCard({ article, navigate }) {
  return (
    <SmartLink
      href={articlePath(article)}
      navigate={navigate}
      className="group overflow-hidden rounded-lg border border-forest/10 bg-paper shadow-sm transition hover:-translate-y-1 hover:shadow-soft"
    >
      <div className="h-44 overflow-hidden">
        <img
          src={article.image}
          alt=""
          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
          loading="lazy"
        />
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-brass">
          <BookOpen className="h-4 w-4" />
          {article.category}
        </div>
        <h3 className="mt-3 text-lg font-bold leading-snug text-forest">{article.title}</h3>
        <p className="mt-3 text-sm leading-6 text-ink/66">{article.description}</p>
        <div className="mt-5 flex items-center justify-between text-sm font-semibold text-forest/60">
          <span>{article.readingTime}</span>
          <ArrowRight className="h-4 w-4 text-wine transition group-hover:translate-x-1" />
        </div>
      </div>
    </SmartLink>
  );
}

function AreaPage({ area, navigate }) {
  const related = areaPages.filter((item) => item.path !== area.path).slice(0, 3);
  return (
    <main>
      <PageHero
        eyebrow={area.eyebrow}
        title={area.title}
        description={area.description}
        image={area.image}
        icon={area.icon}
      />
      <section className="bg-paper py-16 sm:py-20">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[0.72fr_1.28fr] lg:px-8">
          <aside className="lg:sticky lg:top-28 lg:self-start">
            <div className="rounded-lg border border-forest/10 bg-parchment p-6">
              <p className="text-sm font-bold uppercase tracking-[0.18em] text-brass">In sintesi</p>
              <h2 className="mt-3 text-3xl font-extrabold leading-tight text-forest">{area.headline}</h2>
              <p className="mt-4 leading-7 text-ink/70">{area.proof}</p>
              <SmartLink
                href="/contatti"
                navigate={navigate}
                className="mt-6 inline-flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-forest px-5 text-sm font-bold text-white transition hover:bg-pine"
              >
                <CalendarDays className="h-4 w-4" />
                Richiedi appuntamento
              </SmartLink>
            </div>
          </aside>
          <div>
            <h2 className="text-4xl font-extrabold text-forest">Attività svolte</h2>
            <div className="mt-7 grid gap-3 sm:grid-cols-2">
              {area.services.map((service) => (
                <div key={service} className="flex gap-3 rounded-lg border border-forest/10 bg-white p-4">
                  <Check className="mt-1 h-5 w-5 shrink-0 text-brass" />
                  <p className="font-medium leading-6 text-ink/78">{service}</p>
                </div>
              ))}
            </div>
            {area.secondaryServices && (
              <div className="mt-12">
                <h2 className="text-4xl font-extrabold text-forest">{area.secondaryTitle}</h2>
                <div className="mt-7 grid gap-3 sm:grid-cols-2">
                  {area.secondaryServices.map((service) => (
                    <div key={service} className="flex gap-3 rounded-lg border border-forest/10 bg-white p-4">
                      <Check className="mt-1 h-5 w-5 shrink-0 text-brass" />
                      <p className="font-medium leading-6 text-ink/78">{service}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-14 rounded-lg bg-forest p-6 text-white sm:p-8">
              <p className="text-sm font-bold uppercase tracking-[0.18em] text-brass">Percorso</p>
              <h2 className="mt-3 text-4xl font-extrabold">Come viene gestita la pratica</h2>
              <div className="mt-8 grid gap-4 md:grid-cols-2">
                {area.steps.map((step, index) => (
                  <div key={step} className="flex gap-4 rounded-lg border border-white/12 bg-white/8 p-5">
                    <span className="font-serif text-3xl text-brass">0{index + 1}</span>
                    <p className="pt-1 font-medium leading-7 text-white/78">{step}</p>
                  </div>
                ))}
              </div>
            </div>
            <FaqList faqs={area.faqs} />
          </div>
        </div>
      </section>
      <section className="bg-parchment py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionIntro
            eyebrow="Aree correlate"
            title="Altre tutele che possono intrecciarsi con il tuo caso."
            text="Molte vicende non rientrano in una sola categoria. Per questo il sito aiuta a orientarsi senza perdere la visione complessiva."
          />
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {related.map((item) => (
              <SmartLink
                key={item.path}
                href={item.path}
                navigate={navigate}
                className="rounded-lg border border-forest/10 bg-paper p-6 transition hover:-translate-y-1 hover:shadow-soft"
              >
                <Icon name={item.icon} className="h-7 w-7 text-brass" />
                <h3 className="mt-5 text-xl font-bold text-forest">{item.title}</h3>
                <p className="mt-3 text-sm leading-6 text-ink/65">{item.description}</p>
                <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-wine">
                  Approfondisci
                  <ChevronRight className="h-4 w-4" />
                </span>
              </SmartLink>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}

function PageHero({ eyebrow, title, description, image, icon }) {
  return (
    <section className="relative flex min-h-[520px] items-end overflow-hidden bg-forest pt-28 text-white">
      <img src={image} alt="" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-forest via-forest/78 to-forest/24" />
      <div className="relative mx-auto w-full max-w-7xl px-4 pb-14 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-lg bg-paper text-forest shadow-lg">
            <Icon name={icon} className="h-7 w-7" />
          </div>
          <p className="text-sm font-bold uppercase tracking-[0.22em] text-brass">{eyebrow}</p>
          <h1 className="mt-3 text-5xl font-extrabold leading-tight sm:text-6xl">{title}</h1>
          <p className="mt-5 max-w-2xl text-lg leading-8 text-white/82">{description}</p>
        </div>
      </div>
    </section>
  );
}

function FaqList({ faqs }) {
  return (
    <div className="mt-14">
      <h2 className="text-4xl font-extrabold text-forest">Domande frequenti</h2>
      <div className="mt-6 divide-y divide-forest/10 rounded-lg border border-forest/10 bg-white">
        {faqs.map((faq) => (
          <details key={faq.q} className="group p-5 open:bg-parchment/60">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-lg font-bold text-forest">
              {faq.q}
              <ChevronRight className="h-5 w-5 shrink-0 transition group-open:rotate-90" />
            </summary>
            <p className="mt-4 leading-7 text-ink/70">{faq.a}</p>
          </details>
        ))}
      </div>
    </div>
  );
}

function BlogPage({ navigate, articles }) {
  return (
    <main>
      <PageHero
        eyebrow="Blog legale"
        title="Risposte semplici ai dubbi più frequenti."
        description="Leggi le guide dello Studio per capire meglio la tua situazione prima di fissare un appuntamento."
        image={site.stock.civil}
        icon="Landmark"
      />
      <section className="bg-parchment py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
            {articles.map((article) => (
              <ArticleCard key={articlePath(article)} article={article} navigate={navigate} />
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}

function LoadingArticle() {
  return (
    <main className="bg-paper pt-32">
      <section className="mx-auto max-w-3xl px-4 py-24 sm:px-6 lg:px-8">
        <p className="text-sm font-bold uppercase tracking-[0.2em] text-brass">Blog</p>
        <h1 className="mt-4 text-5xl font-extrabold text-forest">Caricamento articolo...</h1>
      </section>
    </main>
  );
}

function ArticlePage({ article, navigate }) {
  return (
    <main>
      <article>
        <section className="bg-forest pt-32 text-white">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 pb-12 sm:px-6 lg:grid-cols-[1fr_430px] lg:px-8">
            <div className="max-w-3xl">
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-brass">{article.category}</p>
              <h1 className="mt-4 text-5xl font-extrabold leading-tight sm:text-6xl">{article.title}</h1>
              <p className="mt-5 text-lg leading-8 text-white/78">{article.description}</p>
              <div className="mt-6 flex flex-wrap gap-3 text-sm font-semibold text-white/70">
                <span>{formatDate(article.date)}</span>
                <span>{article.readingTime}</span>
              </div>
            </div>
            <img
              src={article.image}
              alt=""
              className="aspect-[4/3] w-full rounded-lg object-cover shadow-soft"
              loading="eager"
            />
          </div>
        </section>
        <section className="bg-paper py-16 sm:py-20">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[minmax(0,760px)_1fr] lg:px-8">
            <div className="prose-content">
              {article.contentHtml ? (
                <div dangerouslySetInnerHTML={{ __html: article.contentHtml }} />
              ) : (
                article.content.map((paragraph) => <p key={paragraph}>{paragraph}</p>)
              )}
              <div className="mt-10 rounded-lg border border-forest/10 bg-parchment p-6">
                <h2>Hai bisogno di un orientamento sul tuo caso?</h2>
                <p>
                  Puoi richiedere una prima consulenza conoscitiva per capire documenti, tempi e possibili strade.
                </p>
                <SmartLink
                  href="/contatti"
                  navigate={navigate}
                  className="mt-5 inline-flex h-12 items-center gap-3 rounded-lg bg-forest px-6 text-sm font-bold text-white transition hover:bg-pine"
                >
                  <CalendarDays className="h-4 w-4" />
                  Richiedi appuntamento
                </SmartLink>
              </div>
            </div>
            <aside className="lg:sticky lg:top-28 lg:self-start">
              <div className="rounded-lg border border-forest/10 bg-parchment p-6">
                <p className="text-sm font-bold uppercase tracking-[0.16em] text-brass">Aree legali</p>
                <div className="mt-4 grid gap-2">
                  {areaPages.map((area) => (
                    <SmartLink
                      key={area.path}
                      href={area.path}
                      navigate={navigate}
                      className="flex items-center justify-between rounded-lg bg-paper px-4 py-3 text-sm font-semibold text-forest transition hover:bg-white"
                    >
                      {area.shortTitle}
                      <ChevronRight className="h-4 w-4 text-brass" />
                    </SmartLink>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </section>
      </article>
    </main>
  );
}

function ContactPage() {
  return (
    <main>
      <PageHero
        eyebrow="Contatti"
        title="Richiedi un appuntamento con l’Avvocato Federico Salucci."
        description="Sede principale a Roma, appuntamenti a Viterbo e consulenze online quando il caso lo consente."
        image={site.stock.contactHero}
        icon="Landmark"
      />
      <BookingSection />
      <section className="bg-paper py-16 sm:py-20">
        <div className="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 lg:grid-cols-3 lg:px-8">
          <div className="rounded-lg border border-forest/10 bg-parchment p-6">
            <MapPin className="h-8 w-8 text-brass" />
            <h2 className="mt-5 text-2xl font-bold text-forest">Roma</h2>
            <p className="mt-3 leading-7 text-ink/70">Via Monte Urano n. 47, sede principale dello Studio.</p>
            <a
              href={site.mapUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-wine"
            >
              Apri su Google Maps
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>
          <div className="rounded-lg border border-forest/10 bg-parchment p-6">
            <Users className="h-8 w-8 text-brass" />
            <h2 className="mt-5 text-2xl font-bold text-forest">Viterbo</h2>
            <p className="mt-3 leading-7 text-ink/70">Appuntamenti disponibili previo accordo con lo Studio.</p>
          </div>
          <div className="rounded-lg border border-forest/10 bg-parchment p-6">
            <Globe2 className="h-8 w-8 text-brass" />
            <h2 className="mt-5 text-2xl font-bold text-forest">Online</h2>
            <p className="mt-3 leading-7 text-ink/70">Consulenze da remoto per ridurre tempi e spostamenti quando il caso lo consente.</p>
          </div>
        </div>
      </section>
    </main>
  );
}

function CreditsPage({ navigate }) {
  return (
    <main>
      <section className="relative min-h-[620px] overflow-hidden bg-forest pt-28 text-white">
        <img src={site.stock.rome} alt="" className="absolute inset-0 h-full w-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-r from-forest via-forest/88 to-wine/55" />
        <div className="relative mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-[1fr_420px] lg:items-center lg:px-8">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-brass">Credits</p>
            <h1 className="mt-4 text-5xl font-extrabold leading-tight sm:text-6xl">
              Sito web realizzato da Ioppolo Studio.
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-white/82">
              Progettazione del sito, organizzazione dei contenuti, ottimizzazione SEO e impostazione della presenza
              digitale per {site.brandName}.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href={site.creditsUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-12 items-center justify-center gap-3 rounded-lg bg-brass px-6 text-sm font-bold text-pine transition hover:bg-[#c79b55]"
              >
                Visita Ioppolo Studio
                <ArrowRight className="h-4 w-4" />
              </a>
              <SmartLink
                href="/"
                navigate={navigate}
                className="inline-flex h-12 items-center justify-center gap-3 rounded-lg border border-white/20 bg-white/10 px-6 text-sm font-bold text-white transition hover:bg-white/18"
              >
                Torna al sito
              </SmartLink>
            </div>
          </div>
          <div className="rounded-lg border border-white/14 bg-white/10 p-7 backdrop-blur-xl">
            <Sparkles className="h-10 w-10 text-brass" />
            <h2 className="mt-5 text-2xl font-bold">Ioppolo Studio</h2>
            <p className="mt-4 leading-7 text-white/75">
              Siti internet, consulenza AI, automazioni e formazione per professionisti e aziende che vogliono lavorare meglio con il digitale.
            </p>
            <dl className="mt-6 grid gap-4 border-t border-white/12 pt-6 text-sm">
              <div>
                <dt className="font-bold uppercase tracking-[0.16em] text-white/45">Sito</dt>
                <dd className="mt-1 font-semibold">
                  <a href={site.creditsUrl} target="_blank" rel="noreferrer" className="underline decoration-white/35 underline-offset-4 transition hover:text-brass">
                    ioppolostudio.com
                  </a>
                </dd>
              </div>
              <div>
                <dt className="font-bold uppercase tracking-[0.16em] text-white/45">Cosa fa</dt>
                <dd className="mt-1 font-semibold">Siti web, consulenza AI, automazioni, formazione AI</dd>
              </div>
            </dl>
          </div>
        </div>
      </section>
    </main>
  );
}

function NotFound({ navigate }) {
  return (
    <main className="bg-paper pt-32">
      <section className="mx-auto max-w-3xl px-4 py-24 text-center sm:px-6 lg:px-8">
        <p className="text-sm font-bold uppercase tracking-[0.2em] text-brass">Pagina non trovata</p>
        <h1 className="mt-4 text-5xl font-extrabold text-forest">Il contenuto richiesto non è disponibile.</h1>
        <p className="mt-5 text-lg leading-8 text-ink/70">
          Puoi tornare alla homepage oppure richiedere un appuntamento con lo Studio.
        </p>
        <div className="mt-8 flex justify-center gap-3">
          <SmartLink href="/" navigate={navigate} className="inline-flex h-12 items-center rounded-lg bg-forest px-6 text-sm font-bold text-white">
            Homepage
          </SmartLink>
          <SmartLink href="/contatti" navigate={navigate} className="inline-flex h-12 items-center rounded-lg border border-forest/15 px-6 text-sm font-bold text-forest">
            Contatti
          </SmartLink>
        </div>
      </section>
    </main>
  );
}

function Footer({ navigate }) {
  return (
    <footer className="bg-pine text-white">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:px-6 lg:grid-cols-[1.2fr_0.8fr_0.8fr_0.8fr] lg:px-8">
        <div>
          <Logo navigate={navigate} light />
          <p className="mt-6 max-w-md leading-7 text-white/68">
            Assistenza legale in diritto di famiglia, penale, lavoro, invalidità civile, immigrazione e sinistri stradali.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <a
              href={`tel:${site.phoneHref}`}
              className="inline-flex h-11 items-center gap-2 rounded-lg bg-white px-4 text-sm font-bold text-pine"
            >
              <Phone className="h-4 w-4" />
              {site.phoneLabel}
            </a>
            <a
              href={whatsappUrl('Buongiorno Avv. Salucci, vorrei richiedere un appuntamento.')}
              className="inline-flex h-11 items-center gap-2 rounded-lg border border-white/18 px-4 text-sm font-bold text-white"
            >
              <MessageCircle className="h-4 w-4" />
              WhatsApp
            </a>
          </div>
        </div>
        <FooterColumn
          title="Aree"
          links={areaPages.slice(0, 4).map((area) => ({ label: area.shortTitle, href: area.path }))}
          navigate={navigate}
        />
        <FooterColumn
          title="Studio"
          links={[
            { label: 'Metodo', href: '/#metodo' },
            { label: 'Avvocato', href: '/#avvocato' },
            { label: 'Blog', href: '/blog' },
            { label: 'Contatti', href: '/contatti' }
          ]}
          navigate={navigate}
        />
        <div>
          <p className="text-sm font-bold uppercase tracking-[0.18em] text-brass">Sedi</p>
          <div className="mt-5 space-y-4 text-sm leading-6 text-white/72">
            <p>
              <strong className="text-white">Roma</strong>
              <br />
              Via Monte Urano n. 47
            </p>
            <p>
              <strong className="text-white">Viterbo</strong>
              <br />
              Previo appuntamento
            </p>
          </div>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-6 text-sm text-white/50 sm:px-6 md:flex-row md:items-center md:justify-between lg:px-8">
          <p>© {new Date().getFullYear()} {site.brandName}. Le informazioni presenti nel sito hanno finalità divulgativa.</p>
          <div className="flex flex-wrap gap-4">
            <SmartLink href="/credits" navigate={navigate} className="hover:text-white">
              Credits Ioppolo Studio
            </SmartLink>
            <a href={site.mapUrl} target="_blank" rel="noreferrer" className="hover:text-white">
              Google Maps
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterColumn({ title, links, navigate }) {
  return (
    <div>
      <p className="text-sm font-bold uppercase tracking-[0.18em] text-brass">{title}</p>
      <div className="mt-5 grid gap-3">
        {links.map((link) => (
          <SmartLink key={link.href} href={link.href} navigate={navigate} className="text-sm font-semibold text-white/68 hover:text-white">
            {link.label}
          </SmartLink>
        ))}
      </div>
    </div>
  );
}

function schemaForPath(path, articles = blogArticles) {
  const meta = getMetaForCurrentPath(path, articles);
  const article = findArticleInList(path, articles);
  const schemas = [
    {
      '@context': 'https://schema.org',
      '@type': 'LegalService',
      name: site.brandName,
      url: `${site.siteUrl}${meta.path === '/' ? '/' : meta.path}`,
      image: `${site.siteUrl}${site.portraitImage}`,
      logo: `${site.siteUrl}${site.logo}`,
      telephone: site.phoneHref,
      address: {
        '@type': 'PostalAddress',
        streetAddress: site.address.streetAddress,
        addressLocality: site.address.addressLocality,
        addressCountry: site.address.addressCountry
      },
      areaServed: site.serviceArea,
      founder: {
        '@type': 'Person',
        name: 'Federico Salucci',
        jobTitle: 'Avvocato'
      },
      priceRange: '$$',
      knowsAbout: areaPages.map((area) => area.title)
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: site.brandName,
      url: site.siteUrl,
      publisher: {
        '@type': 'LegalService',
        name: site.brandName
      }
    }
  ];
  if (article) {
    const image = article.image?.startsWith('http') ? article.image : `${site.siteUrl}${article.image}`;
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: article.title,
      description: article.description,
      image,
      datePublished: article.date,
      dateModified: article.date,
      author: {
        '@type': 'Person',
        name: 'Federico Salucci'
      },
      publisher: {
        '@type': 'LegalService',
        name: site.brandName,
        logo: {
          '@type': 'ImageObject',
          url: `${site.siteUrl}${site.logo}`
        }
      },
      mainEntityOfPage: `${site.siteUrl}${articlePath(article)}`,
      articleBody: article.contentHtml ? article.contentHtml.replace(/<[^>]+>/g, ' ') : article.content?.join('\n\n')
    });
  }
  return schemas;
}

function updateStructuredData(path, articles) {
  let script = document.head.querySelector('#structured-data');
  if (!script) {
    script = document.createElement('script');
    script.id = 'structured-data';
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(schemaForPath(path, articles));
}

function formatDate(date) {
  return new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  }).format(new Date(date));
}

export default App;
